// src/pages/Register.js
import { useState, useRef, useContext } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../api/client";
import { AuthContext } from "../auth/AuthContext";
import { Message } from "primereact/message";
import PasswordInput from "../components/PasswordInput";

const S = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(155deg, #0d4f7a 0%, #1a6fa8 45%, #0d9488 100%)",
    padding: "20px 16px",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: 20,
    padding: "36px 32px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  logoWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 24,
    gap: 10,
  },
  logoBox: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandName: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: 20,
    fontWeight: 600,
    color: "#0d4f7a",
    margin: 0,
    textAlign: "center",
  },
  heading: {
    fontSize: 17,
    fontWeight: 600,
    color: "#0f2944",
    margin: "0 0 4px",
    textAlign: "center",
  },
  subheading: {
    fontSize: 13,
    color: "#4a6080",
    margin: "0 0 22px",
    textAlign: "center",
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#4a6080",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: "100%",
    border: "1.5px solid #e2eaf3",
    borderRadius: 10,
    padding: "11px 14px",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    color: "#0f2944",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  },
  row: {
    display: "flex",
    gap: 12,
  },
  btn: {
    width: "100%",
    padding: "13px",
    border: "none",
    borderRadius: 10,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    color: "white",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    marginTop: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  footer: {
    textAlign: "center",
    marginTop: 20,
    fontSize: 13,
    color: "#8fa3bc",
  },
};

function LogoSvg({ size = 28 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <circle cx="50" cy="24" r="10" fill="white"/>
      <line x1="50" y1="34" x2="50" y2="68" stroke="white" strokeWidth="7" strokeLinecap="round"/>
      <polyline points="50,51 30,45 35,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="50,51 70,45 65,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M25,70 Q50,90 75,70" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round"/>
    </svg>
  );
}

export default function Register() {
  const navigate       = useNavigate();
  const { login }      = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  // Present when the person scanned a pharmacy's static QR code, e.g.
  // fitme.lk/register?pharmacy=<org_id>. Carried through OTP verification
  // so the backend can create/link a Customer record for that pharmacy.
  const pharmacyId = searchParams.get("pharmacy") || "";
  const departmentId = searchParams.get("department") || "";

  const [form, setForm] = useState({
    first_name: "", last_name: "",
    email: "", password: "", confirm_password: "",
    gender: "",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setError("");
    if (!form.first_name || !form.last_name) {
      setError("Please enter your full name"); return;
    }
    if (!form.email) {
      setError("Email is required"); return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters"); return;
    }
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match"); return;
    }

    setLoading(true);
    try {
      const res = await api.post("auth/register/", {
        first_name: form.first_name,
        last_name:  form.last_name,
        email:      form.email,
        password:   form.password,
        gender:     form.gender,
      });
      // Backend returns token + phone_verified: false (new account, no phone yet)
      login(res.data.token, false);
      navigate("/verify-phone", { state: { fromRegistration: true, pharmacyId, departmentId } });
    } catch (err) {
      const data = err.response?.data;
      if (data?.email) setError("An account with this email already exists.");
      else setError(data?.error ?? "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (focused) => ({
    ...S.input,
    borderColor: focused ? "#1a6fa8" : "#e2eaf3",
    boxShadow:   focused ? "0 0 0 3px #e8f4fd" : "none",
  });

  const [focused, setFocused] = useState("");

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Lora:wght@600&display=swap" rel="stylesheet" />

      <div style={S.card}>

        <div style={S.logoWrap}>
          <div style={S.logoBox}><LogoSvg size={26} /></div>
          <p style={S.brandName}>FitMe</p>
        </div>

        <p style={S.heading}>Create your account</p>
        <p style={S.subheading}>Join your health portal</p>

        {error && (
          <div style={{ marginBottom: 14 }}>
            <Message severity="error" text={error} style={{ width: "100%", borderRadius: 10 }} />
          </div>
        )}

        <div style={S.row}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>First Name</label>
            <input
              style={inputStyle(focused === "first_name")}
              value={form.first_name}
              onChange={set("first_name")}
              onFocus={() => setFocused("first_name")}
              onBlur={() => setFocused("")}
              placeholder="First"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Last Name</label>
            <input
              style={inputStyle(focused === "last_name")}
              value={form.last_name}
              onChange={set("last_name")}
              onFocus={() => setFocused("last_name")}
              onBlur={() => setFocused("")}
              placeholder="Last"
            />
          </div>
        </div>

        <label style={S.label}>Gender <span style={{ textTransform: "none", fontWeight: 400, color: "#8fa3bc" }}>(optional)</span></label>
        <select
          value={form.gender}
          onChange={set("gender")}
          onFocus={() => setFocused("gender")}
          onBlur={() => setFocused("")}
          style={inputStyle(focused === "gender")}
        >
          <option value="">Prefer not to say</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>

        <label style={S.label}>Email</label>
        <input
          type="email"
          style={inputStyle(focused === "email")}
          value={form.email}
          onChange={set("email")}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused("")}
          placeholder="email@example.com"
        />

        <label style={S.label}>Password</label>
        <PasswordInput
          style={inputStyle(focused === "password")}
          value={form.password}
          onChange={set("password")}
          onFocus={() => setFocused("password")}
          onBlur={() => setFocused("")}
          placeholder="Minimum 8 characters"
        />

        <label style={S.label}>Confirm Password</label>
        <PasswordInput
          style={inputStyle(focused === "confirm_password")}
          value={form.confirm_password}
          onChange={set("confirm_password")}
          onFocus={() => setFocused("confirm_password")}
          onBlur={() => setFocused("")}
          placeholder="Repeat password"
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{ ...S.btn, opacity: loading ? 0.75 : 1 }}
        >
          {loading
            ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 14 }} /> Creating account…</>
            : <><i className="pi pi-user-plus" style={{ fontSize: 14 }} /> Create Account</>
          }
        </button>

        <div style={S.footer}>
          Already have an account?{" "}
          <Link to="/login" style={{ color: "#1a6fa8", fontWeight: 600, textDecoration: "none" }}>
            Sign in
          </Link>
        </div>

      </div>

      <div style={{ marginTop: 24, fontSize: 11.5, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
        Secure customer portal · Powered by Coffer Smart
      </div>
    </div>
  );
}